// Types
interface FieldOption {
  value: string;
  label: string;
}

interface Field {
  name: string;
  label: string;
  placeholder?: string;
  type: string;
  required: boolean;
  readonly?: boolean;
  options?: FieldOption[];
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  showTimeSelect?: boolean;
  disableFutureDates?: boolean;
}

interface Config {
  consumer: Field[];
  site: Field[];
  location: Field[];
  contract: Field[];
  meter: Field[];
}

export const METER_FIELDS_CONFIG: Config = {
  consumer: [
    {
      name: "customerType",
      label: "Consumer Type",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { value: "Personal", label: "Personal" },
        { value: "Business", label: "Business" },
      ],
    },
    {
      name: "customerBillingType",
      label: "Consumer Billing Type",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { value: "Prepaid", label: "Prepaid" },
        { value: "Postpaid", label: "Postpaid" },
      ],
    },
    {
      name: "salutation",
      label: "Salutation",
      placeholder: "Select",
      type: "select",
      options: [
        { value: "Mr", label: "Mr." },
        { value: "Ms", label: "Ms." },
      ],
      required: false,
    },
    {
      name: "firstName",
      label: "First Name",
      placeholder: "Enter first name",
      type: "text",
      required: true,
    },
    {
      name: "lastName",
      label: "Last Name",
      placeholder: "Enter last name",
      type: "text",
      required: true,
    },
    {
      name: "customerCreationDate",
      label: "Consumer Creation Date",
      placeholder: "dd/mm/yyyy",
      type: "date",
      required: false,
      showTimeSelect: false,
      disableFutureDates: true,
    },
    {
      name: "customerBirthDate",
      label: "Consumer Birth Date",
      placeholder: "dd/mm/yyyy",
      type: "date",
      required: false,
      showTimeSelect: false,
      disableFutureDates: true,
    },
    {
      name: "consumerId",
      label: "Consumer ID (external)",
      placeholder: "Enter consumer ID (external)",
      type: "text",
      required: false,
    },
    {
      name: "mobileNumber",
      label: "Mobile Number",
      placeholder: "Mobile Number",
      type: "number",
      required: false,
    },
    {
      name: "emailId",
      label: "Email Id",
      placeholder: "Email Id",
      type: "email",
      required: true,
    },
    {
      name: "billingFrequency",
      label: "Billing Frequency",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { value: "hourly", label: "Hourly" },
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
      ],
    },
    {
      name: "billingLevel",
      label: "Billing Level",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { value: "site", label: "Site" },
        { value: "contract", label: "Contract" },
        { value: "customer", label: "Customer" },
      ],
    },
  ],

  site: [
    {
      name: "status",
      label: "Active/Inactive",
      placeholder: "",
      type: "switch",
      required: false,
      fullWidth: true,
    },
    {
      name: "serviceCommodityType",
      label: "Service Commodity Type",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { label: "Electricity", value: "Electricity" },
        { label: "Water", value: "Water" },
        { label: "Gas", value: "Gas" },
      ],
    },
    {
      name: "serviceType",
      label: "Service Type",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { label: "Commercial", value: "Commercial" },
        { label: "Residential", value: "Residential" },
      ],
    },
    {
      name: "operatingHours",
      label: "Operating Hours",
      placeholder: "Operating Hours",
      type: "range",
      required: false,
    },
    {
      name: "dateOfConnection",
      label: "Date of Connection",
      placeholder: "dd/mm/yyyy",
      type: "date",
      required: false,
      showTimeSelect: false,
    },
    {
      name: "linkToCustomer",
      label: "Link to Consumer",
      placeholder: "Link to Consumer",
      type: "text",
      required: true,
    },
    {
      name: "substationName",
      label: "Substation Name",
      placeholder: "Substation Name",
      type: "text",
      required: false,
    },
    {
      name: "zoneNumber",
      label: "Zone Number",
      placeholder: "Zone Number",
      type: "text",
      required: false,
    },
    {
      name: "feederName",
      label: "Feeder Name",
      placeholder: "Feeder Name",
      type: "text",
      required: false,
    },
    {
      name: "dtrName",
      label: "DTR Name",
      placeholder: "DTR Name",
      type: "text",
      required: false,
    },
    {
      name: "feederPillarNo",
      label: "Feeder Pillar No.",
      placeholder: "Feeder Pillar No.",
      type: "text",
      required: false,
    },
    {
      name: "meterLocation",
      label: "Meter Location",
      placeholder: "Meter Location",
      type: "text",
      required: false,
    },
    {
      name: "changeOfMeterLocation",
      label: "Change of Meter Location Envisaged",
      placeholder: "Change of Meter Location Envisaged",
      type: "text",
      required: false,
    },
  ],

  location: [
    {
      name: "consumerType",
      label: "Consumer Type",
      placeholder: "Select",
      type: "select",
      required: false,
      options: [
        { label: "Personal", value: "Personal" },
        { label: "Business", value: "Business" },
      ],
    },
    {
      name: "houseNo",
      label: "House No.",
      placeholder: "House No.",
      type: "text",
      required: false,
    },
    {
      name: "streetAddress",
      label: "Street Address",
      placeholder: "Street Addressr",
      type: "text",
      required: false,
    },
    {
      name: "city",
      label: "City",
      placeholder: "City",
      type: "text",
      required: false,
    },
    {
      name: "state",
      label: "State",
      placeholder: "State",
      type: "text",
      required: false,
    },
    {
      name: "zipCode",
      label: "Zip Code",
      placeholder: "Zip Code",
      type: "number",
      required: false,
    },
    {
      name: "divisionCode",
      label: "Division Code",
      placeholder: "Division Code",
      type: "text",
      required: false,
    },
    {
      name: "subDivisionCode",
      label: "Sub Division Code",
      placeholder: "Sub Division Code",
      type: "text",
      required: false,
    },
    {
      name: "numberOfMeters",
      label: "Number of Meters",
      placeholder: "Number of Meters",
      type: "number",
      required: false,
    },
  ],

  contract: [
    {
      name: "consumerId",
      label: "Consumer ID",
      placeholder: "Consumer ID",
      type: "text",
      required: false,
    },
    {
      name: "siteId",
      label: "Site ID",
      placeholder: "Site ID",
      type: "text",
      required: false,
    },
    {
      name: "status",
      label: "Active/Inactive",
      placeholder: "",
      type: "switch",
      required: false,
    },
    {
      name: "startDate",
      label: "Contract Start Date",
      placeholder: "dd/mm/yyyy hh:mm",
      type: "date",
      required: false,
    },
    {
      name: "endDate",
      label: "Contract End Date",
      placeholder: "dd/mm/yyyy hh:mm",
      type: "date",
      required: false,
    },
    {
      name: "commodityType",
      label: "Commodity Type",
      placeholder: "Select Commodity Type",
      type: "select",
      required: false,
      options: [
        { value: "Electricity", label: "Electricity" },
        { value: "Water", label: "Water" },
        { value: "Gas", label: "Gas" },
      ],
    },
    {
      name: "commodityCategory",
      label: "Commodity Category",
      placeholder: "Select Commodity Category",
      type: "select",
      required: false,
      options: [
        { value: "Residential", label: "Residential" },
        { value: "Commercial", label: "Commercial" },
      ],
    },
    {
      name: "registerRuleBook",
      label: "Register Rule Book",
      placeholder: "Select Register Rule Book",
      type: "select",
      required: false,
      options: [],
    },
    {
      name: "subscribedVoltage",
      label: "Subscribed voltage",
      placeholder: "Subscribed voltage",
      type: "text",
      required: false,
    },
    {
      name: "subscribedTariff",
      label: "Subscribed Tariff",
      placeholder: "Subscribed Tariff",
      type: "select",
      required: false,
      options: [],
    },
    {
      name: "billingCycle",
      label: "Billing cycle",
      placeholder: "Billing cycle",
      type: "text",
      required: false,
    },
    {
      name: "producerPremise",
      label: "Producer Premise",
      placeholder: "Producer Premise",
      type: "text",
      required: false,
    },
  ],

  meter: [
    {
      name: "connectionStatus",
      label: "Active/Inactive",
      placeholder: "",
      type: "switch",
      required: true,
    },
    {
      name: "useAsLoadProfile",
      label: "Use As Load Profile",
      placeholder: "",
      type: "switch",
      required: false,
    },
    {
      name: "manufacturer",
      label: "Manufacturer",
      placeholder: "Select",
      type: "select",
      required: true,
      options: [
        { value: "SENSUS", label: "SENSUS" },
        { value: "ITRON", label: "ITRON" },
      ],
    },
    {
      name: "meterType",
      label: "Meter Type",
      placeholder: "Select",
      type: "select",
      required: true,
      options: [],
    },
    {
      name: "meterSerialNo",
      label: "Meter Serial No.",
      placeholder: "Meter Serial No.",
      type: "text",
      required: false,
    },
    {
      name: "meterBadgeNo",
      label: "Meter Badge No.",
      placeholder: "Meter Badge No.",
      type: "text",
      required: false,
    },
    {
      name: "meterInstallDate",
      label: "Meter Install Date",
      placeholder: "dd/mm/yyyy",
      type: "date",
      required: false,
    },
    {
      name: "assignToCustomer",
      label: "Assign to Consumer",
      placeholder: "Select a consumer",
      type: "select",
      required: false,
      options: [],
    },
    {
      name: "siteId",
      label: "Site Id",
      placeholder: "Site Id",
      type: "text",
      required: true,
    },
    // {
    //   name: "existingMeterNumber",
    //   label: "Existing Meter Number",
    //   placeholder: "Existing Meter Number",
    //   type: "text",
    //   required: false,
    // },
    {
      name: "lineLtCtRatio",
      label: "Line LT CT Ratio",
      placeholder: "Line LT CT Ratio",
      type: "text",
      required: false,
    },
  ],
};

// Helper function to get only type-specific fields
export const getTypeSpecificConsumerFields = (
  consumerType: keyof Config,
): Field[] => {
  return METER_FIELDS_CONFIG[consumerType] || [];
};
